// src/lib/authority.ts
//
// "May this officer do this to this bill?", asked in the browser with the SAME
// function the gateway asks it with — `authorise()` from `@prs2/auth`, a pure
// exported function this app already depends on at runtime. Before this module
// existed, the lifecycle buttons branched on `bill.status` alone, so an
// HSP_USER holding approval level 0 was offered "Approve (Level 1)" on every
// SUBMITTED bill in scope and got a 403 reading `APPROVE_L1 requires approval
// level 1` — an action token and a level number, telling the officer nothing
// about what to do instead (I7).
//
// ══ THE ONE RULE THIS FILE MUST NEVER BE READ AS BREAKING ══
//
// **This is a courtesy, never a control.** The server is and remains the only
// authority. Every gateway route still performs its own `authorise()` call
// against the session's real scope, and every one of those refusals is still
// rendered verbatim by the pages that call this — see `BillDetailPage`'s
// `lifecycleAttempt` and `ApprovalQueuePage`'s `pending.error`, both of which
// survive untouched. Nothing here makes a server-side check removable, and a
// change that deleted this file entirely would cost usability and zero
// security. The scope this reasons over arrives from `/api/me`; a caller who
// tampered with it in devtools would move buttons around on their own screen
// and change nothing about what the gateway does.
//
// ══ WHY THE DENIAL REASON IS RE-DERIVED RATHER THAN PARSED ══
//
// `authorise()` returns a `reason` written for a developer reading a log
// (`APPROVE_L1 requires approval level 1`). Officers need a different sentence,
// and — as the review put it — "this bill is above your approval limit" is a
// different sentence from "you are not an approver", and both are different
// from "this bill is waiting on someone else". So the ALLOW/DENY verdict comes
// from `authorise()` and only from `authorise()`; the CLASSIFICATION of a
// denial is re-derived here from the same four axes, in the same order. String
// -matching `reason` would have coupled the UI to log wording.
//
// The duplication that buys is exactly one table — `REQUIRED_LEVEL` below —
// and `authority.test.ts` asserts THAT TABLE against `authorise()` itself for
// every action at every approval level a grant can hold, so drift fails the
// suite rather than the screen.
//
// ⚠ THAT CLAIM HAS BEEN FALSE TWICE, in two different ways, and both are kept
// here because both are how a guard quietly stops guarding.
//
// The first: `REQUIRED_LEVEL` was module-private, so the test could not read it
// and declared its own `EXPECTED_LEVEL` — a THIRD copy of the numbers — which it
// checked against `authorise()`. Editing this table left the suite green; the
// guard tested two things that were never the thing at risk. The table is
// exported now and the test imports it, so the assertion is about this file.
//
// The second: the sentence above already said "every action at every level"
// while the test asserted each action at its required level and at ONE below —
// and skipped even that half for `COMPUTE`, which requires level 0 and has
// nothing below it. `COMPUTE` was checked at one level while the comment claimed
// all of them. The test now walks levels 0, 1 and 2 for all five actions and
// asserts `allowed === (level >= required)`, which is what this paragraph says.
import { authorise, type Grant, type Scope, type ScopedBill } from '@prs2/auth'
import { cents } from '@prs2/contracts'
import type { Bill } from './types'

/**
 * The three lifecycle actions this app offers. Deliberately NOT `Action` from
 * `@prs2/auth`: that union also carries `READ` (never a button) and is gaining
 * members as the gateway grows. A local union means a new server-side action
 * cannot silently acquire a half-built client gate — it has to be added here
 * on purpose.
 */
export type LifecycleAction =
  | 'APPROVE_L1'
  | 'APPROVE_L2'
  | 'REJECT'
  // ★★ WAVE 10 — Annex A 37/38's last two limbs. `REQUEST_INFO` pauses a bill,
  // `RESUME` is its return path, `REFER` asks a colleague and moves nothing.
  //
  // ⚠ Added HERE on purpose rather than by widening this union to `@prs2/auth`'s
  // `Action`. This file's own comment says why: a local union means a new
  // server-side action cannot silently acquire a half-built client gate. All three
  // are gated below, and `REQUIRED_LEVEL` has an entry for each — the compiler
  // insisted, because it is a total `Record`.
  | 'REQUEST_INFO'
  | 'RESUME'
  | 'REFER'

/**
 * The two compute operations `POST /api/bills/:reference/compute` serves.
 *
 * ⚠ `COMPUTE_RECOVERY` is not a variant spelling of `COMPUTE`. It writes the
 * money onto a bill that has ALREADY been decided, so it requires level 2 and
 * the monetary ceiling binds it, where the ordinary compute requires level 0
 * and is ceiling-exempt. `packages/auth/src/scope.ts` carries the full
 * argument; this app must gate the repair button on the real answer, because a
 * repair button offered to somebody who will get a 403 is the I7 defect again.
 */
export type ComputeAction = 'COMPUTE' | 'COMPUTE_RECOVERY'

/** Every action this app gates a control on. `READ` is never a button. */
export type GatedAction = LifecycleAction | ComputeAction

/**
 * The approval level each gated action needs — mirrors `REQUIRED_LEVEL` in
 * `packages/auth/src/scope.ts` for these five actions.
 *
 * ⚠ REJECT is level 1, not level 2, and it is level 1 even on a bill awaiting
 * a level 2 decision. That asymmetry is deliberate in `scope.ts` ("refusing a
 * bill is a decision with the same authority as approving it") and it is
 * visible in this UI: on an `AWAITING_L2` bill a level 1 officer is offered
 * Reject and not Approve. Do not "tidy" that into one gate.
 *
 * ⚠ EXPORTED, and that is the point. It was private, and `authority.test.ts`
 * consequently drift-tested a third hardcoded copy of these numbers rather than
 * this table — so editing this table could not fail the suite. The test now
 * imports this very object and asserts each entry against `authorise()`.
 *
 * ⚠ Used ONLY to explain a denial. It never decides one — see the file header.
 */
export const REQUIRED_LEVEL: Readonly<Record<GatedAction, 0 | 1 | 2>> = {
  COMPUTE: 0,
  COMPUTE_RECOVERY: 2,
  APPROVE_L1: 1,
  APPROVE_L2: 2,
  REJECT: 1,
  // ★★ WAVE 10 — level 1 for all three, mirroring `packages/auth/src/scope.ts`.
  //
  // ⚠ AND THE MONETARY CEILING DOES NOT BIND ANY OF THEM, which is why an
  // over-limit bill still offers all three buttons to a level 1 officer while
  // offering neither Approve nor Reject. That looks like an inconsistency on
  // screen and is the deliberate answer: the ceiling asks "may this identity
  // commit the government to this amount", and pausing, resuming or asking a
  // colleague commits nothing. Binding it would leave the officer most likely to
  // spot a problem on an expensive bill unable to ask a single question about it.
  // `scope.ts`'s MONETARY_LIMIT_APPLIES carries the full argument.
  REQUEST_INFO: 1,
  RESUME: 1,
  REFER: 1,
}

/** The decision a bill is currently waiting for, from its status alone. */
export type PendingDecision = 'LEVEL_1' | 'LEVEL_2'

/**
 * Which decision this status is waiting for, or `null` for a bill nothing is
 * waiting on.
 *
 * Mirrors the gateway's own status guards in
 * `services/gateway/src/routes/bills.ts`: `POST .../approve` accepts
 * `SUBMITTED`/`AWAITING_L1`, `POST .../approve-l2` accepts `AWAITING_L2`, and
 * `POST .../reject` accepts `AWAITING_L1`/`AWAITING_L2`. An unrecognised
 * status returns `null` — nothing is offered for a state this build does not
 * know, which is the safe direction.
 */
export function pendingDecision(status: string): PendingDecision | null {
  if (status === 'SUBMITTED' || status === 'AWAITING_L1') return 'LEVEL_1'
  if (status === 'AWAITING_L2') return 'LEVEL_2'
  return null
}

/**
 * Why an action is not offered. Each variant is a genuinely different sentence
 * for the officer, and carries the numbers that sentence needs.
 */
export type Unavailability =
  /** The role holds no grants at all — it can read nothing and decide nothing. */
  | { readonly kind: 'no-grants'; readonly role: string }
  /** Grants exist, but none covers this bill's agency and bill type. */
  | { readonly kind: 'out-of-scope'; readonly agencyCode: string }
  /** Every covering grant is approval level 0 — a reader, not an approver. */
  | { readonly kind: 'not-an-approver'; readonly agencyCode: string }
  /** The officer approves, but one level below what this bill now needs. */
  | { readonly kind: 'wrong-level'; readonly heldLevel: 1; readonly requiredLevel: 2 }
  /** Level is fine; the money is not. `highestLimit` is in integer cents. */
  | { readonly kind: 'over-limit'; readonly highestLimit: number; readonly amount: number }
  /** `bill.amount` is not a whole non-negative number of cents — see `scopedBillFrom`. */
  | { readonly kind: 'unreadable-amount' }
  /**
   * The bill has no pro-ration split yet, and an approval is agreement to a
   * split. Not an authority failure — a precondition failure — but it belongs
   * in the same list because it decides the same thing: whether the button
   * appears.
   */
  | { readonly kind: 'not-computed' }
  /**
   * `authorise()` denied for something this module could not classify. The
   * server's own reason is carried through verbatim rather than replaced by a
   * guess — an unclassified denial is still a denial, and saying so beats
   * inventing a category.
   */
  | { readonly kind: 'refused'; readonly reason: string }

export type ActionAvailability =
  | { readonly allowed: true }
  | { readonly allowed: false; readonly why: Unavailability }

/**
 * Build the `ScopedBill` `authorise()` wants, or `null` when the amount cannot
 * be read as money.
 *
 * `cents()` THROWS on a non-integer or negative value, and this runs during
 * render — so it is guarded rather than called hopefully. A gateway response is
 * the only source of these values today, but a render that throws takes the
 * whole page down, and "deny, and say the amount could not be read" is both
 * survivable and true.
 */
function scopedBillFrom(bill: Bill): ScopedBill | null {
  if (!Number.isSafeInteger(bill.amount) || bill.amount < 0) return null
  return {
    reference: bill.reference,
    agencyCode: bill.agencyCode,
    billType: bill.billType,
    amount: cents(bill.amount),
  }
}

/** The grants that cover this bill's agency and bill type — `authorise()`'s own filter. */
function coveringGrants(scope: Scope, bill: ScopedBill): readonly Grant[] {
  return scope.grants.filter(
    (grant) =>
      grant.agencyCode === bill.agencyCode &&
      (grant.billType === 'ALL' || grant.billType === bill.billType),
  )
}

/**
 * May this scope perform this lifecycle action on this bill, and if not, why —
 * in a shape a sentence can be written from.
 *
 * The verdict is `authorise()`'s. The classification re-walks the same four
 * axes in the same order `authorise()` does, so the branch it lands on is the
 * one that actually stopped the request.
 */
export function availabilityOf(scope: Scope, action: GatedAction, bill: Bill): ActionAvailability {
  const scoped = scopedBillFrom(bill)
  if (scoped === null) return { allowed: false, why: { kind: 'unreadable-amount' } }

  const decision = authorise(scope, action, scoped)
  if (decision.allowed) return { allowed: true }

  if (scope.grants.length === 0) {
    return { allowed: false, why: { kind: 'no-grants', role: scope.role } }
  }

  const covering = coveringGrants(scope, scoped)
  if (covering.length === 0) {
    return { allowed: false, why: { kind: 'out-of-scope', agencyCode: bill.agencyCode } }
  }

  const requiredLevel = REQUIRED_LEVEL[action]
  const heldLevel = covering.reduce((highest, grant) => (grant.approvalLevel > highest ? grant.approvalLevel : highest), 0)
  if (heldLevel < requiredLevel) {
    return heldLevel === 0
      ? { allowed: false, why: { kind: 'not-an-approver', agencyCode: bill.agencyCode } }
      : { allowed: false, why: { kind: 'wrong-level', heldLevel: 1, requiredLevel: 2 } }
  }

  // Reached only when `authorise()` denied, the grants cover this bill, and the
  // level is satisfied — so the money is the only axis left, and it can only be
  // the axis for an action the ceiling actually binds. A ceiling-exempt action
  // (`COMPUTE`) can never arrive here with a `false` verdict, so this branch
  // cannot mislabel one as over-limit.
  const atLevel = covering.filter((grant) => grant.approvalLevel >= requiredLevel)
  const withinLimit = atLevel.some((grant) => grant.approvalLimit === null || bill.amount <= grant.approvalLimit)
  if (!withinLimit) {
    // Every grant in `atLevel` has a non-null limit here — a `null` limit would
    // have satisfied `withinLimit` above — so the reduce below never sees one.
    const highestLimit = atLevel.reduce(
      (highest, grant) => (grant.approvalLimit !== null && grant.approvalLimit > highest ? grant.approvalLimit : highest),
      0,
    )
    return { allowed: false, why: { kind: 'over-limit', highestLimit, amount: bill.amount } }
  }

  // `authorise()` said no and this module cannot say which axis stopped it —
  // possible only if the two ever drift. Carry the server-shaped reason rather
  // than picking a category at random.
  return { allowed: false, why: { kind: 'refused', reason: decision.reason } }
}

/**
 * Everything one bill's Decision surface needs: what it is waiting for, and
 * whether this officer may approve it, reject it, or neither.
 *
 * `approve`/`reject` are `null` — not "denied" — when the bill's STATUS does
 * not admit the action at all. A decided bill offers nothing because there is
 * nothing to offer, which is a different statement from "you may not".
 */
export interface BillAuthority {
  readonly pending: PendingDecision | null
  readonly approve: ActionAvailability | null
  readonly reject: ActionAvailability | null
  /**
   * ★★ WAVE 10 — pause this bill pending information from the HSP.
   *
   * `null` from any status the gateway's own guard refuses — `SUBMITTED` (not in a
   * queue, so no queue to return it to), `APPROVED`/`REJECTED` (decided), and
   * `INFORMATION_REQUESTED` (already paused; {@link resume} is what that state
   * offers).
   */
  readonly requestInfo: ActionAvailability | null
  /**
   * ★★★ WAVE 10 — THE RETURN PATH, and the only action a paused bill offers.
   *
   * ⚠ Non-null EXACTLY when the status is `INFORMATION_REQUESTED`. If this were
   * ever `null` there, the screen would show a bill in a state with no way out —
   * the trap Wave 9 refused to build. `authority.test.ts` asserts that pairing
   * directly rather than trusting this comment.
   */
  readonly resume: ActionAvailability | null
  /** ★★ WAVE 10 — ask a named colleague to look. Offered on every undecided bill, paused or not. */
  readonly refer: ActionAvailability | null
  /**
   * ★★★ True when there is a DECISION here this officer can take — approve or
   * reject. **NOT "is any button rendered"**; see {@link hasAnyControl}.
   *
   * ⚠ **THE DISTINCTION IS WAVE 10's, AND IT WAS FOUND BY A TEST GOING RED.**
   * This flag drives the approval queue's three-way split (`lib/queue.ts`): a row
   * with `hasAnyAction` lands in *"Awaiting your decision"*. When the three new
   * actions were first folded in here, `REFER` — which is legal on almost every
   * undecided bill and needs only level 1 — made this true for nearly every row,
   * and *"Waiting on a pro-ration split"* emptied out. Three cases in
   * `queue.test.ts` failed immediately, which is exactly what they are for.
   *
   * So the meaning is pinned to what the queue actually claims. Asking a
   * colleague to look at a bill is not deciding it, and pausing it is not
   * progress on it — neither belongs in a section headed *"Awaiting your
   * decision"*, and putting them there would tell an officer they have work they
   * do not have.
   *
   * ⚠ `RESUME` is excluded too, and that one is a closer call. A paused bill is
   * waiting on the HSP, not on this office — `pendingDecision()` returns null for
   * it for the same reason — so it is not queue work either. Paused bills are
   * surfaced on the *Your attention* screen instead, which is where a bill that
   * needs a person but not a decision belongs.
   */
  readonly hasAnyAction: boolean
  /**
   * ★★ WAVE 10 — True when ANY of the five controls should be rendered.
   *
   * This is what the bill screen's Decision panel keys on. It is deliberately
   * wider than {@link hasAnyAction}: an officer who cannot approve a bill can
   * still refer it to somebody who can, and a paused bill offers a resume with no
   * decision attached at all. Suppressing the panel in either case would hide the
   * only control the state has — and for a paused bill, that is a state with no
   * way out on screen, which is the trap migration 060 was built to close.
   */
  readonly hasAnyControl: boolean
}

/**
 * ★★ WAVE 10 — the statuses each new action is offered from. Each mirrors the
 * gateway's own guard, in the file named beside it, because a button the server
 * will refuse is the I7 defect this module exists to prevent.
 */
const PAUSABLE_STATUSES: ReadonlySet<string> = new Set(['AWAITING_L1', 'AWAITING_L2'])
const REFERABLE_STATUSES: ReadonlySet<string> = new Set([
  'SUBMITTED',
  'AWAITING_L1',
  'AWAITING_L2',
  'INFORMATION_REQUESTED',
])

export function billAuthority(scope: Scope, bill: Bill): BillAuthority {
  const pending = pendingDecision(bill.status)

  // ★★★ WAVE 10 — COMPUTED BEFORE THE `pending === null` EARLY RETURN, and that
  // is the whole change to this function's shape.
  //
  // ⚠ The early return used to answer "nothing is pending, so nothing is
  // offered", which was true when the only actions were approve and reject.
  // `INFORMATION_REQUESTED` has NO pending decision — `pendingDecision()` returns
  // null for it deliberately, because nobody here is being waited on — and it is
  // the ONE status that must offer `resume`. Leaving the early return in front of
  // these three would have rendered a paused bill with no control at all: a state
  // the officer can enter and not leave, on screen, which is the exact trap
  // migration 060 and `resume()` were built to close. A guard in front of a
  // return path is worse than no guard.
  const requestInfo = PAUSABLE_STATUSES.has(bill.status) ? availabilityOf(scope, 'REQUEST_INFO', bill) : null
  const resume = bill.status === 'INFORMATION_REQUESTED' ? availabilityOf(scope, 'RESUME', bill) : null
  const refer = REFERABLE_STATUSES.has(bill.status) ? availabilityOf(scope, 'REFER', bill) : null
  const secondaryAllowed =
    (requestInfo?.allowed ?? false) || (resume?.allowed ?? false) || (refer?.allowed ?? false)

  if (pending === null) {
    return {
      pending: null,
      approve: null,
      reject: null,
      requestInfo,
      resume,
      refer,
      // ⚠ FALSE — there is no decision to take on a bill nothing is waiting on
      // here. A paused bill must not appear in the queue's "Awaiting your
      // decision" section: it is waiting on the HSP. See the flag's own docstring
      // for the three tests that made this distinction necessary.
      hasAnyAction: false,
      // ⚠ But the PANEL still renders, and for a paused bill it must — Resume is
      // in it.
      hasAnyControl: secondaryAllowed,
    }
  }

  // ⚠ An approval needs a SPLIT to approve, and the database now enforces that:
  // `approve()`/`approveLevel2()` in `packages/persistence/src/bill-repository.ts`
  // both carry `AND b.applied_rule_id IS NOT NULL` in their UPDATE predicate.
  // Without this the screen would offer "Approve (Level 1)" on every uncomputed
  // bill and the write would match no row — surfacing as a 409 reading "its
  // status changed", which is not what happened and tells the officer nothing.
  //
  // ⚠ REJECTION IS NOT GATED THIS WAY, and the asymmetry is deliberate at the
  // database too: `reject()`'s predicate has no such clause. Approving is
  // agreeing to a specific division of a specific amount, so there must be one
  // to agree to; refusing a bill settles it at zero and needs no split.
  //
  // ⚠ AUTHORITY IS CHECKED FIRST, and the order is the whole usefulness of the
  // sentence. A level-0 reader looking at an uncomputed bill must be told they
  // are not an approver — not told to go and compute a split, which is work
  // that would not unblock them. The precondition is only reported to someone
  // it is actually the last thing standing between and the decision.
  const approveAction = pending === 'LEVEL_2' ? 'APPROVE_L2' : 'APPROVE_L1'
  const approveAuthority = availabilityOf(scope, approveAction, bill)
  const approve: ActionAvailability =
    approveAuthority.allowed && bill.appliedRule === null
      ? { allowed: false, why: { kind: 'not-computed' } }
      : approveAuthority
  // ⚠ The gateway's reject route accepts `AWAITING_L1` and `AWAITING_L2` only —
  // never `SUBMITTED`. A submitted bill that has not yet entered the level 1
  // queue cannot be rejected through this API, so no button is offered for it.
  const reject = bill.status === 'AWAITING_L1' || bill.status === 'AWAITING_L2' ? availabilityOf(scope, 'REJECT', bill) : null

  const decidable = approve.allowed || (reject !== null && reject.allowed)
  return {
    pending,
    approve,
    reject,
    requestInfo,
    resume,
    refer,
    // ⚠ UNCHANGED from before Wave 10, deliberately — approve or reject, and
    // nothing else. `lib/queue.ts` reads this to decide what is "yours to decide",
    // and widening it emptied a section. See the docstring.
    hasAnyAction: decidable,
    hasAnyControl: decidable || secondaryAllowed,
  }
}

/** True when this scope may approve OR reject this bill right now. */
export function canActOn(scope: Scope, bill: Bill): boolean {
  return billAuthority(scope, bill).hasAnyAction
}

/**
 * Statuses `POST .../compute` accepts on the ORDINARY path — mirrors
 * `RECOMPUTABLE_STATUSES` in `packages/persistence/src/bill-repository.ts`,
 * where the SQL that enforces them lives. Nothing has been decided about these
 * bills, so computing (or re-computing a mis-keyed amount) is what the stage is
 * for.
 */
const NORMAL_COMPUTE_STATUSES: ReadonlySet<string> = new Set(['SUBMITTED', 'AWAITING_L1'])

/**
 * Statuses the RECOVERY path accepts — mirrors `RECOVERABLE_STATUSES`, and only
 * while the bill has no provenance at all.
 *
 * ⚠ These bills have had a DECISION recorded against them and were never
 * pro-rated: C3's trap. They cannot be approved (both approval routes require
 * `applied_rule_id IS NOT NULL` in SQL), cannot be rejected once APPROVED, and
 * settlement needs a split that does not exist. The repair is the only way out,
 * and until this existed the client offered no route to it at all — the remedy
 * was a curl command.
 */
const RECOVERY_COMPUTE_STATUSES: ReadonlySet<string> = new Set(['AWAITING_L2', 'APPROVED'])

/**
 * On a repair, WHAT HAPPENS NEXT — which is not the same question as who may
 * perform it, and conflating the two put a false sentence on a money screen.
 *
 * ⚠ THE DEFECT THIS EXISTS TO PREVENT. `RECOVERY_COMPUTE_STATUSES` holds TWO
 * statuses, and every sentence written for the repair described only one of
 * them. On an `AWAITING_L2` bill the screen said *"a decision has already been
 * recorded against it"*, *"nobody approves the result afterwards"* and — in the
 * confirm box, directly above the commit button — *"no approval step follows
 * it"*. All three are false there: the bill is waiting on a level 2 decision,
 * and `packages/auth/src/scope.ts` says so in as many words ("on the milder
 * case (`AWAITING_L2`) a level 2 approver still has to decide the bill
 * afterwards and will see the split"). Seen in the browser: the same screen
 * carried "no further approval after it" in one card and "This bill has cleared
 * level 1 and is awaiting a level 2 decision" in the next, with the ledger card
 * between them reading "No decision recorded yet".
 *
 * ⚠ THE AUTHORITY DOES NOT VARY WITH THIS. Both stages need level 2 with the
 * ceiling binding, because the endpoint's authority is set by the worst case in
 * the recoverable set. This type changes only what the officer is TOLD will
 * happen next — never what they may do.
 */
export type RecoveryStage =
  /**
   * `APPROVED` — final. Nothing in this system re-examines the bill, so the
   * subsidy determined by the repair is the figure that reaches settlement.
   */
  | 'FINAL'
  /**
   * `AWAITING_L2` — a level 2 approver still has to decide this bill, and will
   * see the split the repair records.
   */
  | 'AWAITING_LEVEL_2'

/**
 * Which compute this bill would be, and whether this officer may perform it.
 *
 * `null` means the bill is not computable by anyone in any mode — decided AND
 * already pro-rated, rejected, or in a status this build does not know. That is
 * "there is nothing to offer", which is a different statement from "you may
 * not", exactly as `billAuthority` distinguishes them.
 *
 * ⚠ A DISCRIMINATED UNION, not a `kind` beside an optional `stage`. Recovery
 * copy cannot be rendered without deciding which stage it is describing,
 * because that decision is the one the previous version skipped.
 */
export type ComputeOffer =
  | { readonly kind: 'COMPUTE'; readonly availability: ActionAvailability }
  | {
      readonly kind: 'COMPUTE_RECOVERY'
      readonly stage: RecoveryStage
      readonly availability: ActionAvailability
    }

export function computeOffer(scope: Scope, bill: Bill): ComputeOffer | null {
  if (NORMAL_COMPUTE_STATUSES.has(bill.status)) {
    return { kind: 'COMPUTE', availability: availabilityOf(scope, 'COMPUTE', bill) }
  }
  if (RECOVERY_COMPUTE_STATUSES.has(bill.status) && bill.appliedRule === null) {
    return {
      kind: 'COMPUTE_RECOVERY',
      // Read off the status, in the same place the mode itself is read off it —
      // so a third recoverable status cannot be added without answering "and
      // what follows a repair on one of those?".
      stage: bill.status === 'APPROVED' ? 'FINAL' : 'AWAITING_LEVEL_2',
      availability: availabilityOf(scope, 'COMPUTE_RECOVERY', bill),
    }
  }
  return null
}

/**
 * WHY no compute is offered at all — the `computeOffer(...) === null` cases,
 * told apart instead of described by one sentence written for the first of them.
 *
 * ══ THE DEFECT THIS EXISTS TO PREVENT ══
 *
 * `computeOffer` returns `null` for more than one reason and the screen said, of
 * all of them: *"Nothing is owed on a bill in this state, so there is no
 * subsidy/co-payment division to record."* That is true of a REJECTED bill and
 * is a FABRICATED STATEMENT ABOUT MONEY for a status this build does not
 * recognise — a status migrated in under P2C1 §15.2, say — asserted on the
 * screen an officer opens to check exactly that. The comment above the sentence
 * named both cases correctly; the sentence answered for only one.
 *
 * ⚠ Separated the way `discloseIdentityProvider` separates `unrecognised` from
 * the values it knows, and for the same reason: a state this build does not
 * recognise is not a state it may make claims about. "I do not know what this
 * is" is the honest answer and the safe one.
 */
export type NoComputeGround =
  /** Terminal and legitimate: nothing is owed, so there is no split to record. */
  | { readonly kind: 'rejected' }
  /**
   * A rule is recorded but the split is not fully readable — the shape
   * `bill_provenance_consistent` forbids. Nothing is claimed about the money,
   * because the record itself is what looks wrong.
   */
  | { readonly kind: 'inconsistent-provenance' }
  /** A status this build does not know. No claim is made about the bill at all. */
  | { readonly kind: 'unrecognised-status'; readonly status: string }

/**
 * Read the ground off the bill.
 *
 * ⚠ Meaningful only where `computeOffer` returned `null`; call it nowhere else.
 * It is a separate function rather than a fourth `ComputeOffer` variant because
 * `null` already means "there is nothing to offer" at every call site, and
 * widening the union would put a compute-shaped value where no compute exists.
 */
export function noComputeGround(bill: Bill): NoComputeGround {
  if (bill.status === 'REJECTED') return { kind: 'rejected' }
  if (bill.appliedRule !== null) return { kind: 'inconsistent-provenance' }
  return { kind: 'unrecognised-status', status: bill.status }
}

/** The heading and body for {@link noComputeGround}, in the officer's language. */
export interface NoComputeCopy {
  readonly title: string
  readonly body: string
}

export function noComputeCopy(ground: NoComputeGround, statusLabel: string): NoComputeCopy {
  switch (ground.kind) {
    case 'rejected':
      return {
        title: 'Not computed, and no split is needed',
        body:
          `This bill is ${statusLabel} and was never pro-rated. Nothing is owed on a rejected bill, so there is no ` +
          'subsidy/co-payment division to record: this is a state of the bill, not a missing feature.',
      }
    case 'inconsistent-provenance':
      return {
        title: 'This bill’s pro-ration record is incomplete',
        body:
          'A pro-ration rule is recorded against this bill but the subsidy and co-payment are not, which is a ' +
          'combination the database forbids. No figure here can be trusted and no compute is offered, because ' +
          'the record itself looks wrong rather than unfinished. Report the reference to the service desk.',
      }
    case 'unrecognised-status':
      // ⚠ NO CLAIM ABOUT MONEY. This build does not know what this status means,
      // so it does not know whether anything is owed, and saying "nothing is
      // owed" here would be inventing an answer on a money screen.
      return {
        title: 'No pro-ration split, and this build cannot say whether one is needed',
        body:
          `This bill's status is "${ground.status}", which this build does not recognise: it was added to the ` +
          'system after this screen was written. Whether a bill in that state needs a subsidy/co-payment ' +
          'division is not something this screen can answer, so it offers no compute and makes no claim either ' +
          'way. Report the reference to the service desk.',
      }
  }
}

/**
 * How this bill came to need a repair rather than an ordinary compute. One
 * clause, two truths — never "decided", which is only true of one of them.
 */
export function recoveryPremise(stage: RecoveryStage): string {
  return stage === 'FINAL'
    ? 'This bill was approved without ever being pro-rated'
    : 'This bill cleared level 1 without ever being pro-rated'
}

/**
 * What the repair commits the Authority to, and what still stands between it
 * and settlement.
 *
 * ⚠ The level 2 requirement is stated as unconditional in BOTH branches, because
 * it is. The `AWAITING_LEVEL_2` wording must not read as "and so a lower level
 * would do here" — it explains why the stricter of the two answers governs, per
 * `scope.ts`.
 */
export function recoveryStakes(stage: RecoveryStage): string {
  return stage === 'FINAL'
    ? 'no approval follows it, so the figure reaches settlement unchecked, which is why it needs level 2 approval authority'
    : 'a level 2 approver still has to decide this bill and will see the split, but the repair needs level 2 authority regardless, because the same action on an already-approved bill would reach settlement unchecked'
}

/**
 * The officer-facing sentence for a compute the officer cannot perform.
 *
 * Separate from {@link unavailabilitySentence} rather than folded into it: the
 * approval sentences are written about deciding a bill ("this bill is waiting on
 * someone else", "an approver at X has to decide it"), and none of those
 * sentences is true of a repair. A repair is not waiting on anyone — it is a
 * correction to a record that is already wrong, and the officer needs to be told
 * that and told who can make it.
 */
export function computeUnavailabilitySentence(
  why: Unavailability,
  // ⚠ The OFFER, not a bare `kind`. A bare `kind` let a caller pair a `why`
  // taken from one offer with a mode taken from nowhere — and, once recovery
  // grew a second stage, made it possible to render recovery copy without ever
  // deciding which stage was being described. That is the defect `RecoveryStage`
  // exists to prevent; passing the offer is what makes it unrepresentable.
  offer: ComputeOffer,
  formatMoney: (cents: number) => string,
): string {
  if (offer.kind === 'COMPUTE') {
    switch (why.kind) {
      case 'no-grants':
        return `Your active identity carries no bill grants at all, so it cannot compute anything. Ask your agency administrator to grant you a scope, or switch to an identity that holds one.`
      case 'out-of-scope':
        return `None of your grants cover ${why.agencyCode} bills of this type, so you cannot compute this one.`
      case 'unreadable-amount':
        return `This bill's amount could not be read as a money value, so there is nothing to pro-rate against. Report the reference to the service desk: the record itself looks wrong.`
      default:
        // Computing an ordinary bill needs no approval level and is exempt from
        // the monetary ceiling, so no other axis can refuse it. Carrying the
        // server's own words beats inventing a category for a case that should
        // not arise.
        return why.kind === 'refused' ? why.reason : `You cannot compute this bill.`
    }
  }

  // ⚠ Composed from {@link recoveryPremise}/{@link recoveryStakes} rather than
  // written out per branch, so the stage-dependent facts exist ONCE. The
  // previous version stated "decided", "no further approval between" and "no
  // approval after it" as flat facts in four separate branches — all four false
  // on an `AWAITING_L2` bill, and four places to miss when fixing them.
  const premise = recoveryPremise(offer.stage)
  const stakes = recoveryStakes(offer.stage)

  switch (why.kind) {
    case 'no-grants':
      return `${premise}, so the split has to be recorded as a repair, and your active identity carries no bill grants at all. Ask your agency administrator, or switch to an identity that holds a level 2 approval scope.`
    case 'out-of-scope':
      return `${premise}, so recording the split now is a repair rather than an ordinary compute. None of your grants cover ${why.agencyCode} bills of this type, so a level 2 approver for ${why.agencyCode} has to make it.`
    case 'not-an-approver':
      return `${premise}. Recording the split now is a repair, not an ordinary compute: ${stakes}. Your grants for ${why.agencyCode} carry none, so a level 2 approver has to make this repair.`
    case 'wrong-level':
      return `${premise}. Recording the split now is a repair, and ${stakes}. Your grants reach level 1, so a level 2 approver has to make this repair.`
    case 'over-limit':
      // ⚠ No counterfactual in the AWAITING_L2 branch. The first draft explained
      // the ceiling by describing what the same repair would do on an
      // ALREADY-APPROVED bill — true, and exactly the sort of clause an officer
      // moving quickly reads as a statement about the bill in front of them. The
      // suite caught it. The reason a level 2 ceiling binds here is enough on its
      // own; the officer does not need the worse case to act on this one.
      return `This repair is above your approval limit. The bill is ${formatMoney(why.amount)} and the highest limit on any of your level 2 grants is ${formatMoney(why.highestLimit)}. ${
        offer.stage === 'FINAL'
          ? 'Because this bill is already approved, recording the split commits the Government to that amount with nothing approving it afterwards'
          : 'A repair carries level 2 authority with that limit binding, in either of the states a repair is possible in'
      }, so an approver whose limit reaches it has to make it.`
    case 'unreadable-amount':
      return `This bill's amount could not be read as a money value, so your authority to repair it cannot be checked here. Report the reference to the service desk: the record itself looks wrong.`
    case 'not-computed':
      // Unreachable: `not-computed` is a precondition of APPROVING, and this is
      // the action that satisfies it. Answered rather than left to fall through.
      return `This bill has no pro-ration split, which is what this repair records.`
    case 'refused':
      return why.reason
  }
}

/**
 * The officer-facing sentence for an unavailable action.
 *
 * Written to answer "what do I do now?", which a bare `APPROVE_L1 requires
 * approval level 1` does not. Money is formatted by the caller's own
 * `formatCents` so this module stays free of presentation imports; the numbers
 * arrive already in integer cents.
 */
export function unavailabilitySentence(
  why: Unavailability,
  action: LifecycleAction,
  formatMoney: (cents: number) => string,
): string {
  const verb = action === 'REJECT' ? 'reject' : 'approve'
  switch (why.kind) {
    case 'no-grants':
      return `Your active identity carries no bill grants at all, so it cannot ${verb} anything. Ask your agency administrator to grant you an approval scope, or switch to an identity that holds one.`
    case 'out-of-scope':
      return `None of your grants cover ${why.agencyCode} bills of this type, so you cannot ${verb} this one. An officer whose grants cover ${why.agencyCode} has to decide it.`
    case 'not-an-approver':
      return `You are not an approver for ${why.agencyCode}. Your grants let you read this bill but carry no approval authority, so an approver at ${why.agencyCode} has to decide it: you cannot ${verb} it yourself.`
    case 'wrong-level':
      return `This bill is waiting on someone else. It has cleared level 1 and now needs a level 2 approver; your grants reach level 1, so there is nothing for you to approve here.`
    case 'over-limit':
      return `This bill is above your approval limit. It is ${formatMoney(why.amount)} and the highest limit on any of your grants that reach this decision is ${formatMoney(why.highestLimit)}. An approver with a higher limit has to decide it.`
    case 'unreadable-amount':
      return `This bill's amount could not be read as a money value, so your authority to ${verb} it cannot be checked here. Report the reference to the service desk: the record itself looks wrong.`
    case 'not-computed':
      // ⚠ NO NAVIGATION INSTRUCTION, and no "compute". This sentence renders on
      // BOTH the approval queue and the bill's own Decision card, and on the
      // latter it read "Open the bill and compute the split first" to an officer
      // already looking at that bill — directly under a panel calling the very
      // same act a REPAIR. Seen in the browser on an AWAITING_L2 bill with no
      // split. The queue keeps its own "Open a bill to record the split" in the
      // section subtitle, which is where a navigation instruction belongs and is
      // true; this sentence now states only the precondition, in the vocabulary
      // that is correct in both compute modes.
      return `This bill has no pro-ration split yet, and approving it means agreeing to a specific division of the amount between subsidy and co-payment. The split has to be recorded on the bill first: the amount, the rule and the rate all have to exist before there is anything to approve.`
    case 'refused':
      return why.reason
  }
}
